import { AttributionDateRange, ChannelStat, InfluencerRecord, SourceDetail } from "./types"

export const formatCurrency = (amount: number, currency = "USD", locale = "en-US") => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(amount)
}

export const buildOrdersPageHref = (
  integrationId: string,
  dateRange: Pick<AttributionDateRange, "month_start_utc" | "month_end_utc">,
  productIds: number[] = [],
) => {
  const params = new URLSearchParams({
    page: "shopify-orders",
    integration_id: integrationId,
    month_start_utc: dateRange.month_start_utc.toISOString(),
    month_end_utc: dateRange.month_end_utc.toISOString(),
  })
  if (productIds.length > 0) params.set("product_ids", productIds.join(","))
  return `/?${params.toString()}`
}

export const cloneSourceDetail = (detail: SourceDetail): SourceDetail => ({
  ...detail,
  utmMetrics: [...(detail.utmMetrics || [])],
  orderIds: [...(detail.orderIds || [])],
  discountCodes: detail.discountCodes ? detail.discountCodes.map((item) => ({ ...item })) : undefined,
  children: detail.children ? detail.children.map((child) => cloneSourceDetail(child)) : undefined,
  archivedAt: detail.archivedAt ?? null,
  isArchived: detail.isArchived ?? false,
})

export const createPlaceholderCodeDetail = (
  influencer: InfluencerRecord,
  code: string,
  archivedAt?: string | null,
): SourceDetail => {
  const normalizedCode = code.trim().toUpperCase()
  return {
    sourceKey: `code-${normalizedCode}`,
    sourceLabel: normalizedCode,
    sourceName: normalizedCode,
    orders: 0,
    revenue: 0,
    averageOrderValue: 0,
    orderIds: [],
    utmMetrics: [],
    discountCodeUsed: normalizedCode,
    discountCodes: [],
    actualCode: normalizedCode,
    influencerId: influencer.id,
    influencerName: influencer.name,
    influencerCode: normalizedCode,
    children: undefined,
    expense: 0,
    costPerOrder: 0,
    roi: 0,
    archivedAt: archivedAt ?? null,
    isArchived: Boolean(archivedAt),
    selfExpense: 0,
  }
}

export const enrichStatsWithInfluencers = (
  rawStats: ChannelStat[],
  influencerList: InfluencerRecord[],
): ChannelStat[] => {
  if (!Array.isArray(rawStats) || rawStats.length === 0) {
    return influencerList.length === 0 ? rawStats || [] : rawStats
  }

  const statsClone = rawStats.map((stat) => ({
    ...stat,
    sources: (stat.sources || []).map((source) => cloneSourceDetail(source)),
  }))

  const influencerIndex = statsClone.findIndex(
    (stat) => stat.channelSlug === "influencers",
  )

  if (influencerIndex === -1) {
    return statsClone
  }

  const influencerChannel = statsClone[influencerIndex]
  const sourceMap = new Map<string, SourceDetail>()

  influencerChannel.sources.forEach((source, index) => {
    influencerChannel.sources[index] = {
      ...source,
      children: source.children ? source.children.map((child) => cloneSourceDetail(child)) : [],
    }
    const normalizedId = source.influencerId || ""
    if (normalizedId) {
      sourceMap.set(normalizedId, influencerChannel.sources[index])
    }
  })

  for (const influencer of influencerList) {
    const existingSource = sourceMap.get(influencer.id)

    if (!existingSource) {
      const children = influencer.codes.map((code) => createPlaceholderCodeDetail(influencer, code.code, code.archived_at))

      const placeholder: SourceDetail = {
        sourceKey: `influencer-${influencer.id}`,
        sourceLabel: influencer.name,
        sourceName: influencer.name,
        orders: 0,
        revenue: 0,
        averageOrderValue: 0,
        orderIds: [],
        utmMetrics: [],
        discountCodeUsed: null,
        discountCodes: [],
        actualCode: null,
        influencerId: influencer.id,
        influencerName: influencer.name,
        influencerCode: null,
        children,
        expense: 0,
        costPerOrder: 0,
        roi: 0,
        archivedAt: influencer.archived_at ?? null,
        isArchived: Boolean(influencer.archived_at),
        selfExpense: 0,
      }

      influencerChannel.sources.push(placeholder)
      sourceMap.set(influencer.id, placeholder)
      continue
    }

    const childMap = new Map<string, SourceDetail>()
    existingSource.children = (existingSource.children || []).map((child) => {
      const clonedChild = cloneSourceDetail(child)
      const key = clonedChild.influencerCode || clonedChild.actualCode || clonedChild.sourceLabel
      if (key) {
        childMap.set(key.toUpperCase(), clonedChild)
      }
      return clonedChild
    })

    for (const code of influencer.codes) {
      const codeKey = code.code.trim().toUpperCase()
      if (!childMap.has(codeKey)) {
        const placeholderChild = createPlaceholderCodeDetail(influencer, code.code, code.archived_at)
        existingSource.children?.push(placeholderChild)
        childMap.set(codeKey, placeholderChild)
        continue
      }

      const existingChild = childMap.get(codeKey)
      if (existingChild) {
        existingChild.archivedAt = code.archived_at ?? null
        existingChild.isArchived = Boolean(code.archived_at)
      }
    }

    if (existingSource.children) {
      existingSource.children = existingSource.children.sort((a, b) => a.sourceLabel.localeCompare(b.sourceLabel))
    }

    existingSource.influencerName = influencer.name
    existingSource.archivedAt = influencer.archived_at ?? null
    existingSource.isArchived = Boolean(influencer.archived_at)
    existingSource.selfExpense = existingSource.selfExpense ?? 0
  }

  influencerChannel.sources = influencerChannel.sources.sort((a, b) => a.sourceLabel.localeCompare(b.sourceLabel))

  return statsClone
}
